import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';

import {
  IliasObject,
  LmsPlacement,
  LmsRegistration,
  LmsRegistryService,
} from '../../../services/lms-registry.service';
import { Tutor } from '../../../services/tutor.service';

/** Minimal course shape from `GET /courses/client-courses` (al-filemanager). */
export interface LtiPlacementDialogCourse {
  id: string;
  course_name: string;
}

/**
 * Create / edit one LTI placement: (course context [+ activity]) → agent
 * [+ AlphaTutor course]. Create POSTs `/lms/placements` with `bot_id`;
 * edit PATCHes `bot_id` / `course_id`. The LMS identifiers are frozen in
 * edit mode — they are the row's identity (partial unique index in
 * migration 036), changing them means a different placement.
 */
@Component({
  selector: 'app-lti-placement-dialog',
  templateUrl: './lti-placement-dialog.component.html',
  styleUrl: './lti-placement-dialog.component.scss',
})
export class LtiPlacementDialogComponent implements OnInit {
  @Input() registration!: LmsRegistration;
  @Input() placement: LmsPlacement | null = null;
  @Input() tutors: Tutor[] = [];
  @Input() courses: LtiPlacementDialogCourse[] = [];
  @Input() clientId: string | null = null;
  /** Vorbelegte Kontext-ID beim Anlegen. Die ILIAS-Karte kennt den Kurs der
   *  Zeile bereits — ohne sie müsste die ref_id abgetippt werden, obwohl sie
   *  eine Spalte weiter steht. Beim Bearbeiten wirkungslos. */
  @Input() presetContextId: string | null = null;

  form!: FormGroup;
  saving = false;
  errorMessage: string | null = null;

  /** Synchronisierte ILIAS-Kurse und -Gruppen; leer, solange nie gesynct wurde. */
  iliasObjects: IliasObject[] = [];
  objectsLoading = false;
  /** Manuelle Eingabe trotz vorhandener Liste — für Kurse, die der letzte Sync
   *  noch nicht kannte. Ohne diesen Ausweg wäre ein frischer Kurs bis zum
   *  nächsten Sync unerreichbar. */
  manualEntry = false;

  /** Der vorbelegte Kurs wurde verworfen, es wird doch ausgewählt. */
  changingContext = false;

  constructor(
    private fb: FormBuilder,
    private lms: LmsRegistryService,
    private toastr: NbToastrService,
    private t: TranslateService,
    protected dialogRef: NbDialogRef<LtiPlacementDialogComponent>,
  ) {}

  get isEdit(): boolean {
    return !!this.placement;
  }

  /** Haengt die Verbindung nur am Plugin, gibt es keinen Launch — und damit
   *  keine Aktivitaeten. Der Plugin-Abruf nimmt ausschliesslich kursweite
   *  Zeilen; eine mit Aktivitaets-ID waere dort unsichtbar, also wird das
   *  Feld gar nicht erst angeboten. */
  get hidesResourceLink(): boolean {
    return LmsRegistryService.isPluginOnlyIlias(this.registration);
  }

  /** „LTI-Platzierung" stimmt nur, wo LTI im Spiel ist. Bei einer reinen
   *  Plugin-Verbindung heisst dasselbe schlicht Platzierung. */
  get titleKey(): string {
    const base = this.hidesResourceLink
      ? 'pages.tutors.placements.plain'
      : 'pages.tutors.placements.lti';
    return `${base}.${this.isEdit ? 'editTitle' : 'createTitle'}`;
  }

  /** Der Kurs steht schon fest.
   *
   *  Wer in der Kursliste auf „Platzieren" gedrückt hat, hat den Kurs damit
   *  gewählt. Ihm danach eine Liste aller Kurse vorzulegen ist eine zweite
   *  Frage nach derselben Sache — und sie ging bisher sogar schief: die
   *  Vorbelegung stand zwar im Formular, aber `nb-select` bekam sie, bevor
   *  seine Optionen geladen waren, und zeigte deshalb den Platzhalter. Aus
   *  „genau dieser Kurs" wurde so „bitte nochmal wählen".
   */
  get contextFixed(): boolean {
    return !this.isEdit && !!this.presetContextId && !this.changingContext;
  }

  /** Auswahlliste statt Freitext — nur wenn es wirklich etwas auszuwählen gibt. */
  get usePicker(): boolean {
    return !this.isEdit && !this.contextFixed && !this.manualEntry && this.iliasObjects.length > 0;
  }

  /** Beschriftung des feststehenden Kurses. Bis die Objektliste da ist, steht
   *  die ref_id da — besser als eine leere Zeile. */
  get fixedContextLabel(): string {
    const id = String(this.presetContextId ?? '');
    const hit = this.iliasObjects.find((o) => String(o.external_id) === id);
    return hit ? this.objectLabel(hit) : `ref_id ${id}`;
  }

  /** Doch einen anderen Kurs nehmen — der Ausweg, nicht der Normalfall. */
  changeContext(): void {
    this.changingContext = true;
    this.form.get('lti_context_id')?.setValue('');
  }

  objectLabel(obj: IliasObject): string {
    const kind = obj.object_type === 'grp'
      ? this.t.instant('pages.tutors.placements.ilias.picker.group')
      : this.t.instant('pages.tutors.placements.ilias.picker.course');
    return `${obj.title} · ${kind} · ${obj.path || '—'}`;
  }

  useManualEntry(): void {
    this.manualEntry = true;
    this.form.get('lti_context_id')?.setValue('');
  }

  usePickerAgain(): void {
    this.manualEntry = false;
    this.form.get('lti_context_id')?.setValue('');
  }

  private loadIliasObjects(): void {
    if (this.isEdit || !LmsRegistryService.isIlias(this.registration)) return;
    this.objectsLoading = true;
    this.lms.listIliasObjects(this.registration.id).subscribe({
      next: (rows) => {
        this.iliasObjects = rows;
        this.objectsLoading = false;
      },
      // Ein fehlgeschlagener Abruf darf das Anlegen nicht verhindern: ohne
      // Liste bleibt das Freitextfeld, und das funktioniert weiterhin.
      error: () => {
        this.iliasObjects = [];
        this.objectsLoading = false;
      },
    });
  }

  ngOnInit(): void {
    const p = this.placement;
    const boundCourse =
      p?.course_id && (!this.clientId || p.course_id !== this.clientId) ? p.course_id : null;
    this.form = this.fb.group({
      lti_context_id: [{ value: p?.lti_context_id ?? this.presetContextId ?? '', disabled: this.isEdit }, Validators.required],
      lti_resource_link_id: [{ value: p?.lti_resource_link_id ?? '', disabled: this.isEdit }],
      bot_id: [p?.bot_id ?? null, Validators.required],
      course_id: [boundCourse],
    });
    this.loadIliasObjects();
  }

  tutorLabel(tutor: Tutor): string {
    const visual = (tutor.visual_name || '').trim();
    return visual && visual !== tutor.name ? `${tutor.name} · ${visual}` : tutor.name;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving) return;
    this.saving = true;
    this.errorMessage = null;

    const v = this.form.getRawValue();
    const request$ = this.isEdit
      ? this.lms.updatePlacement(this.placement!.id, {
          bot_id: v.bot_id,
          // Unbinding a course = back to the client_id placeholder. The
          // PATCH model reads `null` as "not provided", so send the
          // placeholder explicitly.
          course_id: v.course_id || this.clientId || undefined,
        })
      : this.lms.createPlacement({
          registration_id: this.registration.id,
          lti_context_id: String(v.lti_context_id).trim(),
          lti_resource_link_id: String(v.lti_resource_link_id || '').trim() || null,
          bot_id: v.bot_id,
          course_id: v.course_id || null,
        });

    request$.subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success(
          this.t.instant(
            this.isEdit
              ? 'pages.tutors.placements.lti.updatedToast'
              : 'pages.tutors.placements.lti.createdToast',
          ),
          '',
        );
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        const detail = err?.error?.detail;
        this.errorMessage =
          (typeof detail === 'string' && detail) ||
          detail?.message ||
          this.t.instant('toasts.error');
      },
    });
  }
}
